import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import type {
  BridgeAttachment,
  BridgeCommand,
  BridgeClientRequestType,
  BridgeClientReportCreateResponse,
  BridgeMessage,
  BridgeMessageListResponse,
  BridgeMessagePostResponse,
} from "@tuxx/orbit-bridge";
import { useOrbit } from "../provider";
import type { OrbitProjectActivityRow } from "../types";
import {
  createRecoveredAttachmentState,
  getAttachmentFallbackBody,
  getComposerSubmitState,
  getDeliveryReceiptText,
  type DeliveryReceiptStatus,
  type DraftAttachmentSource,
  type DraftAttachmentState,
} from "./attachmentDraft";
import {
  listRecoverableAttachments,
  removeRecoverableAttachment,
  saveRecoverableAttachment,
} from "./attachmentRecovery";

type ComposerType = "message" | BridgeClientRequestType;
type ReportSeverity = "low" | "medium" | "high" | "urgent";
type ClientDiagnosticEvent = {
  type: "error" | "unhandledrejection";
  message: string;
  timestamp: string;
  source?: string;
};

interface OrbitInboxPanelProps {
  recordingMinimized?: boolean;
  onMinimizeForRecording?: () => void;
  onRestoreFromRecording?: () => void;
  onRecordingChange?: (recording: boolean) => void;
}

interface DraftAttachment extends DraftAttachmentState {
  localId: string;
  file?: File;
}

interface TasksResponse {
  ok: boolean;
  activity?: OrbitProjectActivityRow[];
}

type StreamMessageItem = {
  kind: "message";
  id: string;
  timestamp: string | null;
  message: BridgeMessage;
};

type StreamUpdateItem = {
  kind: "update";
  id: string;
  timestamp: string | null;
  title: string;
  body: string | null;
  label: string;
};

type StreamItem = StreamMessageItem | StreamUpdateItem;
type BridgeMessageSource = "client" | "orion" | "team";

type ReadyDraftAttachment = DraftAttachment & {
  status: "ready";
  uploadId: string;
  storagePath: string;
};

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024 * 1024;

const COMPOSER_TYPES: Array<{ value: ComposerType; label: string; placeholder: string }> = [
  {
    value: "message",
    label: "Message",
    placeholder: "Message your Orbit team...",
  },
  {
    value: "bug_report",
    label: "Bug",
    placeholder: "What happened? Add steps, expected behaviour, and what you tried...",
  },
  {
    value: "question",
    label: "Question",
    placeholder: "Ask a question and add any context we need...",
  },
  {
    value: "change_request",
    label: "Request",
    placeholder: "Describe the change, desired outcome, and priority...",
  },
  {
    value: "approval",
    label: "Approval",
    placeholder: "Confirm what you are approving or add any conditions...",
  },
  {
    value: "milestone_confirmation",
    label: "Milestone",
    placeholder: "Confirm the milestone and add any notes...",
  },
];

export function OrbitInboxPanel({
  recordingMinimized = false,
  onMinimizeForRecording,
  onRestoreFromRecording,
  onRecordingChange,
}: OrbitInboxPanelProps) {
  const inputId = useId();
  const orbit = useOrbit();
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingTracksRef = useRef<MediaStreamTrack[]>([]);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const [composerType, setComposerType] = useState<ComposerType>("message");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<ReportSeverity>("medium");
  const [messages, setMessages] = useState<BridgeMessage[]>([]);
  const [projectUpdates, setProjectUpdates] = useState<OrbitProjectActivityRow[]>([]);
  const [attachments, setAttachments] = useState<BridgeAttachment[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [draftAttachments, setDraftAttachments] = useState<DraftAttachment[]>([]);
  const [diagnosticEvents, setDiagnosticEvents] = useState<ClientDiagnosticEvent[]>([]);
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryReceiptStatus>("idle");
  const [lastSentAttachmentCount, setLastSentAttachmentCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientNotice, setClientNotice] = useState<{ title: string; message: string } | null>(null);

  const selectedType = COMPOSER_TYPES.find((item) => item.value === composerType) ?? COMPOSER_TYPES[0];
  const isRequest = composerType !== "message";
  const uploading = draftAttachments.some((attachment) => attachment.status === "uploading");
  const submitState = getComposerSubmitState({
    busy,
    recording,
    isRequest,
    subject,
    body,
    attachments: draftAttachments,
  });
  const canSubmit = submitState.canSubmit;
  const receiptStatus: DeliveryReceiptStatus = busy
    ? "sending"
    : uploading
      ? "uploading"
      : deliveryStatus === "sent" || deliveryStatus === "failed"
        ? deliveryStatus
        : draftAttachments.some((attachment) => attachment.status === "ready")
          ? "ready"
          : "idle";
  const receiptAttachmentCount = receiptStatus === "sent" ? lastSentAttachmentCount : draftAttachments.length;
  const receiptText = submitState.reason ?? getDeliveryReceiptText(receiptStatus, receiptAttachmentCount);
  const streamItems = buildStreamItems(messages, projectUpdates);
  const latestUpdateItems = streamItems.filter((item): item is StreamUpdateItem => item.kind === "update").slice(-4).reverse();
  const newestStreamItemId = streamItems[streamItems.length - 1]?.id ?? "";
  const streamEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || !streamItems.length) return;
    const scrollToBottom = () => {
      thread.scrollTop = thread.scrollHeight;
      streamEndRef.current?.scrollIntoView({ block: "end" });
    };
    scrollToBottom();
    const frame = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(frame);
  }, [streamItems.length, newestStreamItemId]);

  useEffect(() => {
    const pushDiagnostic = (event: ClientDiagnosticEvent) => {
      setDiagnosticEvents((current) => [...current.slice(-24), event]);
    };
    const onError = (event: ErrorEvent) => {
      pushDiagnostic({
        type: "error",
        message: event.message || "Unknown browser error",
        source: event.filename,
        timestamp: new Date().toISOString(),
      });
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      pushDiagnostic({
        type: "unhandledrejection",
        message: event.reason instanceof Error ? event.reason.message : String(event.reason || "Unhandled promise rejection"),
        timestamp: new Date().toISOString(),
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  const loadInbox = useCallback(async () => {
    try {
      const [messageResult, attachmentResult, taskResult] = await Promise.allSettled([
        orbit.call<BridgeMessageListResponse>("messages.list", { limit: 60 }),
        orbit.attachments.list({ scope: "message", limit: 50 }),
        orbit.call<TasksResponse>("tasks.list"),
      ]);

      if (messageResult.status === "fulfilled") {
        setMessages(messageResult.value.messages);
      } else {
        throw messageResult.reason;
      }

      if (attachmentResult.status === "fulfilled") {
        setAttachments(attachmentResult.value.attachments);
      }

      if (taskResult.status === "fulfilled") {
        setProjectUpdates(taskResult.value.activity ?? []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Orbit inbox");
    } finally {
      setInboxLoading(false);
    }
  }, [orbit]);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    const listener = orbit.commands.listen(
      async (command: BridgeCommand) => {
        if (command.command_type !== "chat.notify") return { ignored: true };

        const title = typeof command.payload.title === "string" ? command.payload.title : "New Orbit update";
        const message = typeof command.payload.message === "string" ? command.payload.message : "Open your Orbit inbox for the latest update.";

        setClientNotice({ title, message });
        await loadInbox();
        return { displayed: true };
      },
      { target: "browser", pollIntervalMs: 8_000 },
    );

    void listener.tick();
    return () => listener.stop();
  }, [loadInbox, orbit]);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") recorder.stop();
      stopRecordingTracks();
    };
  }, []);

  useEffect(() => {
    onRecordingChange?.(recording);
  }, [onRecordingChange, recording]);

  const stopRecordingTracks = () => {
    recordingTracksRef.current.forEach((track) => track.stop());
    recordingTracksRef.current = [];
  };

  const trackBridgeEvent = useCallback(
    async (eventName: string, payload: Record<string, unknown>) => {
      try {
        await orbit.track(eventName, payload, { metadata: { source: "orbit_client_panel" } });
      } catch {
        // Telemetry should never block the client from sending a support request.
      }
    },
    [orbit],
  );

  const persistRecoverableAttachment = useCallback(
    async (localId: string, file: File, source: DraftAttachmentSource, caption: string) => {
      try {
        await saveRecoverableAttachment({
          localId,
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          byteSize: file.size,
          caption,
          source,
          createdAt: Date.now(),
          blob: file,
        });
      } catch (err) {
        void trackBridgeEvent("attachment_recovery_save_failed", {
          reason: err instanceof Error ? err.message : "Failed to save local recovery copy",
          file_name: file.name,
          byte_size: file.size,
          source,
        });
      }
    },
    [trackBridgeEvent],
  );

  const forgetRecoverableAttachment = useCallback(
    async (localId: string) => {
      try {
        await removeRecoverableAttachment(localId);
      } catch (err) {
        void trackBridgeEvent("attachment_recovery_remove_failed", {
          reason: err instanceof Error ? err.message : "Failed to remove local recovery copy",
          local_id: localId,
        });
      }
    },
    [trackBridgeEvent],
  );

  useEffect(() => {
    let cancelled = false;

    const recoverAttachments = async () => {
      try {
        const records = await listRecoverableAttachments();
        if (cancelled || !records.length) return;

        setDraftAttachments((current) => {
          const existingIds = new Set(current.map((attachment) => attachment.localId));
          const recovered = records
            .filter((record) => !existingIds.has(record.localId))
            .map<DraftAttachment>((record) => {
              const file = new File([record.blob], record.fileName, {
                type: record.contentType || "application/octet-stream",
                lastModified: record.createdAt,
              });
              return {
                ...createRecoveredAttachmentState({
                  localId: record.localId,
                  fileName: record.fileName,
                  byteSize: record.byteSize,
                  caption: record.caption,
                  source: record.source,
                }),
                localId: record.localId,
                file,
              };
            });
          return recovered.length ? [...recovered, ...current] : current;
        });
        setDeliveryStatus("failed");
        setError("Recovered unsent Orbit files. Retry upload, then send them.");
        void trackBridgeEvent("attachment_recovery_loaded", { count: records.length });
      } catch (err) {
        void trackBridgeEvent("attachment_recovery_load_failed", {
          reason: err instanceof Error ? err.message : "Failed to load local recovery copies",
        });
      }
    };

    void recoverAttachments();
    return () => {
      cancelled = true;
    };
  }, [trackBridgeEvent]);

  const uploadDraftFile = async (file: File, source: DraftAttachmentSource, existingLocalId?: string) => {
    const localId = existingLocalId || createLocalId();
    const caption = source === "screen_recording" ? "Screen recording with audio" : "Client attachment";

    if (file.size > MAX_ATTACHMENT_BYTES) {
      const message = "Files must be 2 GB or smaller.";
      setError(message);
      setDeliveryStatus("failed");
      setDraftAttachments((current) => [
        ...current,
        {
          localId,
          file,
          status: "failed",
          progress: 0,
          fileName: file.name,
          byteSize: file.size,
          caption,
          source,
          error: message,
        },
      ]);
      void trackBridgeEvent("attachment_upload_failed", {
        reason: "file_too_large",
        file_name: file.name,
        byte_size: file.size,
        source,
      });
      return;
    }

    const uploadingDraft: DraftAttachment = {
      localId,
      file,
      status: "uploading",
      progress: 1,
      fileName: file.name,
      byteSize: file.size,
      caption,
      source,
    };

    setDraftAttachments((current) =>
      existingLocalId ? current.map((item) => (item.localId === existingLocalId ? uploadingDraft : item)) : [...current, uploadingDraft],
    );
    setDeliveryStatus("uploading");
    setError(null);
    void persistRecoverableAttachment(localId, file, source, caption);
    void trackBridgeEvent("attachment_upload_started", {
      file_name: file.name,
      byte_size: file.size,
      content_type: file.type || "application/octet-stream",
      source,
    });

    try {
      const upload = await orbit.attachments.createUpload({
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
        byte_size: file.size,
        scope: "message",
        metadata: { source },
      });

      await uploadToSignedUrl(upload.signed_url, file, (progress) => {
        setDraftAttachments((current) =>
          current.map((item) => (item.localId === localId ? { ...item, progress: Math.min(95, progress) } : item)),
        );
      });

      setDraftAttachments((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                file: undefined,
                status: "ready",
                progress: 100,
                error: undefined,
                uploadId: upload.upload_id,
                storagePath: upload.path,
              }
            : item,
        ),
      );
      setDeliveryStatus("ready");
      void trackBridgeEvent("attachment_upload_ready", {
        upload_id: upload.upload_id,
        file_name: file.name,
        byte_size: file.size,
        source,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      setDeliveryStatus("failed");
      setDraftAttachments((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                file,
                status: "failed",
                progress: 0,
                error: message,
              }
            : item,
        ),
      );
      void trackBridgeEvent("attachment_upload_failed", {
        reason: message,
        file_name: file.name,
        byte_size: file.size,
        source,
      });
    }
  };

  const retryDraftAttachment = (attachment: DraftAttachment) => {
    if (!attachment.file) {
      setError("This upload cannot be retried. Remove it and record or attach the file again.");
      return;
    }
    void uploadDraftFile(attachment.file, attachment.source, attachment.localId);
  };

  const removeDraftAttachment = (localId: string) => {
    setDraftAttachments((current) => current.filter((attachment) => attachment.localId !== localId));
    void forgetRecoverableAttachment(localId);
  };

  const isReadyDraftAttachment = (attachment: DraftAttachment): attachment is ReadyDraftAttachment => {
    return attachment.status === "ready" && Boolean(attachment.uploadId && attachment.storagePath);
  };

  const linkDraftAttachments = async (messageId: string | null, conversationId: string | null, taskId?: string | null) => {
    if (!draftAttachments.length) return;
    const readyAttachments = draftAttachments.filter(isReadyDraftAttachment);
    if (readyAttachments.length !== draftAttachments.length) {
      throw new Error("Wait for all uploads to finish before sending.");
    }

    await Promise.all(
      readyAttachments.map((attachment) =>
        orbit.attachments.complete({
          upload_id: attachment.uploadId,
          storage_path: attachment.storagePath,
          scope: "message",
          message_id: messageId || undefined,
          conversation_id: conversationId || undefined,
          task_id: taskId || undefined,
          caption: attachment.caption,
          metadata: { source: attachment.source },
        }),
      ),
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    const cleanSubject = subject.trim();
    const cleanBody = body.trim();
    const fallbackBody = getAttachmentFallbackBody(draftAttachments);
    const requestTitle =
      cleanSubject ||
      (draftAttachments.some((attachment) => attachment.source === "screen_recording")
        ? "Screen recording"
        : draftAttachments.length > 0
          ? "Client attachment"
          : "Client request");
    const baseMetadata = {
      source: "orbit_client_panel",
      has_attachments: draftAttachments.length > 0,
      attachment_sources: draftAttachments.map((attachment) => attachment.source),
    };

    setBusy(true);
    setDeliveryStatus("sending");
    setError(null);
    try {
      const sentAttachmentCount = draftAttachments.length;
      let messageId: string | null = null;
      let conversationId: string | null = null;

      if (isRequest) {
        const response: BridgeClientReportCreateResponse = await orbit.reports.create({
          type: composerType as BridgeClientRequestType,
          title: requestTitle,
          body: cleanBody || fallbackBody || undefined,
          severity,
          diagnostics: collectClientDiagnostics(diagnosticEvents),
          metadata: { ...baseMetadata, intake_state: "needs_orbit_review" },
        });
        messageId = response.request.message_id;
        conversationId = response.request.conversation_id;
        await linkDraftAttachments(messageId, conversationId, response.task_id);
      } else {
        const response = await orbit.call<BridgeMessagePostResponse>("messages.post", {
          subject: cleanSubject || undefined,
          body: cleanBody || fallbackBody,
          metadata: baseMetadata,
        });
        messageId = response.message_id;
        conversationId = response.conversation_id;
        await linkDraftAttachments(messageId, conversationId);
      }

      if (draftAttachments.length) {
        void trackBridgeEvent("message_sent_with_attachments", {
          message_id: messageId,
          conversation_id: conversationId,
          attachment_count: draftAttachments.length,
          attachment_sources: draftAttachments.map((attachment) => attachment.source),
        });
        void Promise.all(draftAttachments.map((attachment) => forgetRecoverableAttachment(attachment.localId)));
      }
      setSubject("");
      setBody("");
      setLastSentAttachmentCount(sentAttachmentCount);
      setDraftAttachments([]);
      setDeliveryStatus("sent");
      await loadInbox();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send to Orbit";
      setError(message);
      setDeliveryStatus("failed");
      void trackBridgeEvent("message_send_failed", {
        reason: message,
        attachment_count: draftAttachments.length,
        attachment_statuses: draftAttachments.map((attachment) => attachment.status),
      });
    } finally {
      setBusy(false);
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === "undefined") {
      const message = "Screen recording is not supported in this browser.";
      setError(message);
      void trackBridgeEvent("screen_recording_failed", { reason: message });
      return;
    }

    setError(null);
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      let micStream: MediaStream | null = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        micStream = null;
      }

      const tracks = [
        ...displayStream.getVideoTracks(),
        ...displayStream.getAudioTracks(),
        ...(micStream?.getAudioTracks() ?? []),
      ];
      const recordingStream = new MediaStream(tracks);
      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(recordingStream, mimeType ? { mimeType } : undefined);
      const chunks: BlobPart[] = [];

      recorderRef.current = recorder;
      recordingTracksRef.current = tracks;
      setRecording(true);
      void trackBridgeEvent("screen_recording_started", {
        has_screen_audio: displayStream.getAudioTracks().length > 0,
        has_microphone_audio: (micStream?.getAudioTracks().length ?? 0) > 0,
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || "video/webm";
        const blob = new Blob(chunks, { type });
        const file = new File([blob], createRecordingFileName(type), { type });

        recorderRef.current = null;
        stopRecordingTracks();
        setRecording(false);
        onRestoreFromRecording?.();

        if (blob.size > 0) {
          void trackBridgeEvent("screen_recording_stopped", {
            byte_size: blob.size,
            content_type: type,
          });
          void uploadDraftFile(file, "screen_recording");
        } else {
          const message = "Recording stopped before any video data was captured. Please try recording again.";
          setError(message);
          void trackBridgeEvent("screen_recording_failed", { reason: "empty_blob" });
        }
      };

      displayStream.getVideoTracks()[0]?.addEventListener("ended", stopRecording, { once: true });
      recorder.start(1000);
      onMinimizeForRecording?.();
    } catch (err) {
      setRecording(false);
      stopRecordingTracks();
      const message =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Screen or microphone permission was denied. Nothing was recorded."
          : err instanceof Error
            ? err.message
            : "Screen recording failed";
      setError(message);
      void trackBridgeEvent("screen_recording_failed", {
        reason: message,
        error_name: err instanceof DOMException ? err.name : undefined,
      });
    }
  };

  if (recordingMinimized) {
    return (
      <div className="orbit-client-inbox orbit-client-inbox--recording">
        <div className="orbit-recording-strip">
          <span className="orbit-recording-strip__dot" aria-hidden />
          <div>
            <strong>Recording screen and audio</strong>
            <span>Talk through the issue, then stop recording to attach it to your draft.</span>
          </div>
          <button type="button" className="orbit-button orbit-button--secondary" onClick={onRestoreFromRecording}>
            Open draft
          </button>
          <button type="button" className="orbit-button" onClick={stopRecording}>
            Stop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="orbit-client-inbox">
      {clientNotice ? (
        <div className="orbit-client-notice" role="status" aria-live="polite">
          <div>
            <strong>{clientNotice.title}</strong>
            <span>{clientNotice.message}</span>
          </div>
          <button type="button" onClick={() => setClientNotice(null)} aria-label="Dismiss notification">
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="orbit-client-inbox__thread-shell">
        <div className="orbit-client-inbox__stream-layout">
          <div ref={threadRef} className="orbit-client-inbox__thread" aria-label="Orbit conversation and project updates">
            {inboxLoading && streamItems.length === 0 ? (
              <OrbitInboxSkeleton />
            ) : streamItems.length > 0 ? (
              streamItems.map((item) => {
                if (item.kind === "message") {
                  const copy = formatClientFacingBridgeMessage(item.message);
                  const source = bridgeMessageSource(item.message);
                  return (
                    <article key={item.id} className={`orbit-chat-message orbit-chat-message--${item.message.direction} orbit-chat-message--source-${source}`}>
                      <div className="orbit-chat-message__bubble">
                        <div className="orbit-chat-message__meta">
                          <span className={`orbit-chat-message__sender-chip orbit-chat-message__sender-chip--${source}`}>
                            {messageSenderLabel(item.message)}
                          </span>
                          <span>{formatRelative(item.timestamp)}</span>
                        </div>
                        {copy.subject ? <strong className="orbit-chat-message__subject">{copy.subject}</strong> : null}
                        <div className="orbit-chat-message__body">{copy.body}</div>
                        {item.message.attachments?.length ? (
                          <div className="orbit-chat-message__attachments" aria-label="Message attachments">
                            {item.message.attachments.map((attachment, index) => {
                              const label = attachment.file_name || attachment.filename || `Attachment ${index + 1}`;
                              return attachment.signed_url ? (
                                <a
                                  key={attachment.id || attachment.bridge_attachment_id || `${label}-${index}`}
                                  href={attachment.signed_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {label}
                                </a>
                              ) : (
                                <span key={attachment.id || attachment.bridge_attachment_id || `${label}-${index}`}>{label}</span>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                }

                return (
                  <article key={item.id} className="orbit-chat-message orbit-chat-message--outbound orbit-chat-message--update">
                    <div className="orbit-chat-message__bubble">
                      <div className="orbit-chat-message__meta">
                        <span>Orion</span>
                        <span>{formatRelative(item.timestamp)}</span>
                      </div>
                      <span className="orbit-chat-message__badge">{item.label}</span>
                      <strong className="orbit-chat-message__subject">{item.title}</strong>
                      {item.body ? <div className="orbit-chat-message__body">{item.body}</div> : null}
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="orbit-client-inbox__empty">
                <span className="orbit-client-inbox__empty-mark" aria-hidden>
                  O
                </span>
                <strong>Start a stream with Orbit</strong>
                <span>Project updates, completed work, questions, files, and replies will appear here like one chat.</span>
              </div>
            )}
            <div ref={streamEndRef} className="orbit-client-inbox__stream-end" aria-hidden />
          </div>

          <aside className="orbit-client-inbox__summary" aria-label="Recent project update summary">
            <span className="orbit-client-inbox__summary-kicker">Latest from the team</span>
            <strong>What changed recently</strong>
            {latestUpdateItems.length ? (
              <ul>
                {latestUpdateItems.map((item) => (
                  <li key={`summary-${item.id}`}>
                    <span>{item.label}</span>
                    {item.body || item.title}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Updates from project work will appear here as they are shared.</p>
            )}
          </aside>
        </div>
      </div>

      <form className="orbit-composer" onSubmit={submit}>
        <div className="orbit-composer__quickbar">
          <div className="orbit-composer__types" role="radiogroup" aria-label="Message type">
            {COMPOSER_TYPES.map((item) => (
              <button
                key={item.value}
                type="button"
                className="orbit-composer__type"
                aria-pressed={composerType === item.value}
                onClick={() => setComposerType(item.value)}
                disabled={busy}
              >
                {item.label}
              </button>
            ))}
          </div>
          {receiptText ? <div className="orbit-composer__status">{receiptText}</div> : null}
        </div>

        {isRequest ? (
          <div className="orbit-composer__diagnostics">
            <span>Report will include page context, browser details, recent errors, and attached recordings/files.</span>
            <label>
              Priority
              <select
                value={severity}
                disabled={busy}
                onChange={(event) => setSeverity(event.currentTarget.value as ReportSeverity)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
          </div>
        ) : null}

        <div className="orbit-composer__box">
          <input
            type="text"
            className="orbit-composer__subject"
            placeholder={isRequest ? "Title this request" : "Subject (optional)"}
            value={subject}
            disabled={busy}
            maxLength={200}
            onChange={(event) => setSubject(event.currentTarget.value)}
          />
          <textarea
            className="orbit-composer__textarea"
            placeholder={selectedType.placeholder}
            value={body}
            disabled={busy}
            rows={2}
            maxLength={10_000}
            onChange={(event) => setBody(event.currentTarget.value)}
          />

          {draftAttachments.length > 0 ? (
            <ul className="orbit-composer__attachments" aria-label="Files attached to this draft">
              {draftAttachments.map((attachment) => (
                <li
                  key={attachment.localId}
                  className={`orbit-composer__attachment orbit-composer__attachment--${attachment.status}`}
                >
                  <span>{attachment.fileName}</span>
                  <small>{formatDraftAttachmentStatus(attachment)}</small>
                  {attachment.status === "failed" ? (
                    <button type="button" onClick={() => retryDraftAttachment(attachment)} disabled={busy}>
                      Retry
                    </button>
                  ) : null}
                  <button type="button" onClick={() => removeDraftAttachment(attachment.localId)} disabled={busy}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {error ? <div className="orbit-feedback__error">{error}</div> : null}

          <div className="orbit-composer__footer">
            <div className="orbit-composer__tools">
              <label className="orbit-composer__tool" htmlFor={inputId}>
                {uploading ? "Uploading..." : "Attach"}
              </label>
              <button
                type="button"
                className="orbit-composer__tool"
                onClick={recording ? stopRecording : startRecording}
                disabled={busy || uploading}
              >
                {recording ? "Stop recording" : "Record screen + audio"}
              </button>
              <input
                id={inputId}
                className="orbit-attachments__input"
                type="file"
                multiple
                disabled={busy || uploading}
                onChange={(event) => {
                  const files = Array.from(event.currentTarget.files || []);
                  event.currentTarget.value = "";
                  void Promise.all(files.map((file) => uploadDraftFile(file, "file")));
                }}
              />
            </div>
            <button type="submit" className="orbit-button" disabled={!canSubmit}>
              {busy ? "Sending..." : isRequest ? "Submit request" : "Send"}
            </button>
          </div>
        </div>

        {recording ? (
          <div className="orbit-recording-strip">
            <span className="orbit-recording-strip__dot" aria-hidden />
            <div>
              <strong>Recording screen and audio</strong>
              <span>You can minimize Orbit while you talk through the issue.</span>
            </div>
            <button type="button" className="orbit-button orbit-button--secondary" onClick={onMinimizeForRecording}>
              Minimize
            </button>
            <button type="button" className="orbit-button" onClick={stopRecording}>
              Stop
            </button>
          </div>
        ) : null}

        {attachments.length > 0 ? (
          <div className="orbit-composer__recent-files">
            {attachments.slice(0, 3).map((attachment) => (
              <a key={attachment.id} href={attachment.signed_url} target="_blank" rel="noreferrer">
                {attachment.content_type?.startsWith("video/") ? "Recording" : "File"}: {attachment.file_name}
              </a>
            ))}
          </div>
        ) : null}
      </form>
    </div>
  );
}

function OrbitInboxSkeleton() {
  return (
    <div className="orbit-client-inbox__loading" aria-busy="true" aria-live="polite">
      <span className="orbit-taskboard__loading-label">Loading inbox…</span>
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="orbit-client-inbox__skeleton-row" aria-hidden>
          <span className="orbit-client-inbox__skeleton-avatar" />
          <div className="orbit-client-inbox__skeleton-copy">
            <div className="orbit-taskboard__skeleton-line" />
            <div className="orbit-taskboard__skeleton-line orbit-taskboard__skeleton-line--short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function getRecordingMimeType(): string {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ].find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function createRecordingFileName(mimeType: string): string {
  const extension = mimeType.includes("mp4") ? "mp4" : "webm";
  const stamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace("T", " ")
    .replace(":", "-");
  return `Screen recording - ${stamp}.${extension}`;
}

function uploadToSignedUrl(signedUrl: string, file: File, onProgress: (progress: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("x-upsert", "false");

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      onProgress(Math.max(1, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onerror = () => reject(new Error("Upload failed. Check your connection and retry."));
    xhr.ontimeout = () => reject(new Error("Upload timed out. Check your connection and retry."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with HTTP ${xhr.status}`));
      }
    };

    const formData = new FormData();
    formData.append("cacheControl", "3600");
    formData.append("", file);
    xhr.send(formData);
  });
}

function createLocalId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function collectClientDiagnostics(events: ClientDiagnosticEvent[]): Record<string, unknown> {
  const navigatorWithConnection = navigator as Navigator & {
    connection?: { effectiveType?: string; downlink?: number; rtt?: number };
  };
  const connection = navigatorWithConnection.connection ?? null;

  return {
    url: safeBrowserUrl(window.location.href),
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer ? safeBrowserUrl(document.referrer) : null,
    captured_at: new Date().toISOString(),
    browser: {
      user_agent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      online: navigator.onLine,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      device_pixel_ratio: window.devicePixelRatio,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
    },
    network: connection
      ? {
          effective_type: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
        }
      : null,
    recent_events: events.slice(-25).map((event) => ({
      ...event,
      message: redactDiagnosticString(event.message),
      source: event.source ? safeBrowserUrl(event.source) : undefined,
    })),
  };
}

function safeBrowserUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return redactDiagnosticString(value.split("?")[0]?.split("#")[0] || value);
  }
}

function redactDiagnosticString(value: string): string {
  return value
    .replace(/([?&](?:[^=&#]*(?:token|secret|password|passwd|api[-_]?key|access[-_]?key|refresh|jwt|session)[^=&#]*)=)[^&#\s]+/gi, "$1[redacted]")
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi, "$1 [redacted]")
    .replace(/\b(?:sk|pk|rk|whsec)_(?:live|test)_[A-Za-z0-9_]+/g, "[redacted_key]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[redacted_jwt]")
    .slice(0, 1_000);
}

function buildStreamItems(messages: BridgeMessage[], updates: OrbitProjectActivityRow[]): StreamItem[] {
  const messageItems: StreamItem[] = messages.map((message) => ({
    kind: "message",
    id: `message-${message.id}`,
    timestamp: message.received_at || message.sent_at,
    message,
  }));

  const updateItems: StreamItem[] = updates
    .filter((update) => Boolean(update.title))
    .slice(0, 12)
    .map((update) => {
      const copy = buildClientUpdateCopy(update);
      return {
        kind: "update",
        id: `update-${update.id}`,
        timestamp: update.created_at,
        title: copy.title,
        body: copy.body,
        label: copy.label,
      };
    });

  return [...messageItems, ...updateItems]
    .sort((left, right) => timestampValue(left.timestamp) - timestampValue(right.timestamp))
    .slice(-80);
}

function timestampValue(value: string | null): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function streamUpdateLabel(update: OrbitProjectActivityRow): string {
  if (update.action_type === "repo_pull_request") return "Review";
  if (update.action_type === "repo_push" || update.action_type === "repo_commit") return "Progress";
  if (update.action_type === "task_status_changed") return "Completed";
  if (update.action_type.includes("task")) return "Work";
  return "Update";
}

function buildClientUpdateCopy(update: OrbitProjectActivityRow): { title: string; body: string | null; label: string } {
  const rawTitle = cleanStreamText(update.title) || "Project update";
  const rawSummary = cleanStreamText(update.summary);
  const label = streamUpdateLabel(update);
  const work = normalizeClientWorkText(rawSummary || rawTitle);

  if (update.action_type === "task_status_changed" || /\b(complete|completed|done|finished|shipped)\b/i.test(rawTitle)) {
    return {
      label: "Completed",
      title: completedClientTitle(work),
      body: completedClientBenefit(work),
    };
  }

  if (/\b(blocker|blocked|verify|validation|check|audit)\b/i.test(rawTitle)) {
    return {
      label: "Checking",
      title: "Checking an important project item",
      body: `We are checking ${work} before marking it complete. This helps prevent avoidable issues reaching your live workflow.`,
    };
  }

  if (update.action_type === "repo_pull_request") {
    return {
      label,
      title: "Work is ready for review",
      body: `We have prepared ${work} for review. Once it is approved, it can move into the next delivery step.`,
    };
  }

  if (update.action_type === "repo_push" || update.action_type === "repo_commit") {
    return {
      label,
      title: clientUpdateTitle(work),
      body: `We updated ${work}. This keeps the project moving and helps improve the experience your team uses day to day.`,
    };
  }

  return {
    label,
    title: clientUpdateTitle(work),
    body: rawSummary
      ? normalizeClientSentence(normalizeClientWorkText(rawSummary))
      : `We updated ${work}. This keeps the project moving forward and is now visible in this stream.`,
  };
}

function formatClientFacingBridgeMessage(message: BridgeMessage): { subject: string | null; body: string } {
  const rawSubject = message.subject ?? "";
  const rawBody = message.body_text ?? "";
  const combined = `${rawSubject}\n${rawBody}`;
  const isOrbitGenerated = message.direction === "outbound" && /^(orbit|)$/i.test(message.from_name || "");
  const hasLegacyInternalCopy =
    /\bOrbit project board\b/i.test(combined) ||
    /\bcompleted workstream\b/i.test(combined) ||
    /\bCompleted on \d{4}-\d{2}-\d{2}\b/i.test(combined) ||
    /^Completed:/i.test(rawSubject) ||
    /^Completed:/i.test(rawBody);

  if (!isOrbitGenerated || !hasLegacyInternalCopy) {
    return {
      subject: rawSubject.trim() || null,
      body: rawBody.trim(),
    };
  }

  const cleanedSubject = cleanStreamText(rawSubject);
  const cleanedBody = cleanStreamText(rawBody);
  const work = normalizeClientWorkText(cleanedSubject || cleanedBody || "the latest project work");

  return {
    subject: completedClientTitle(work),
    body: completedClientBenefit(work),
  };
}

function bridgeMessageMetadata(message: BridgeMessage): Record<string, unknown> {
  const metadata = message.metadata;
  return metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {};
}

function metadataString(metadata: Record<string, unknown>, key: string): string {
  const value = metadata[key];
  return typeof value === "string" ? value.trim() : "";
}

function bridgeMessageSource(message: BridgeMessage): BridgeMessageSource {
  if (message.direction !== "outbound") return "client";
  const metadata = bridgeMessageMetadata(message);
  const identity = metadataString(metadata, "sender_identity").toLowerCase();
  const label = metadataString(metadata, "sender_label") || (message.from_name || "").trim();

  if (identity === "orion" || /^orion$/i.test(label) || /^orbit$/i.test(label)) return "orion";
  return "team";
}

function messageSenderLabel(message: BridgeMessage): string {
  if (message.direction !== "outbound") return message.from_name || "You";
  const metadata = bridgeMessageMetadata(message);
  const source = bridgeMessageSource(message);
  if (source === "orion") return "Orion";
  return metadataString(metadata, "sender_label") || (message.from_name || "").trim() || "TUXX team";
}

function cleanStreamText(value: string | null): string | null {
  if (!value) return null;
  const cleaned = value
    .replace(/\[[^\]]+\]\s*/g, "")
    .replace(/^Completed:\s*/i, "")
    .replace(/\bCompleted on \d{4}-\d{2}-\d{2}\.?\s*/gi, "")
    .replace(/\bThis is now marked as done on your Orbit project board\.?\s*/gi, "")
    .replace(/\bThis has been moved into the completed workstream so you can see what is now done\.?\s*/gi, "")
    .replace(/^Pushed\s+[a-f0-9]{6,40}:\s*/i, "")
    .replace(/^Commit\s+[a-f0-9]{6,40}:\s*/i, "")
    .replace(/^(fix|feat|docs|chore|refactor|test|style|perf|build|ci)(\([^)]+\))?!?:\s*/i, "")
    .replace(/\b(HARC|Aurum|Orbit)\s+(complete|prod blocker|calendar|admin)\b[:\s-]*/gi, "")
    .replace(/^Historical completion from full git history\.?$/i, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return null;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function normalizeClientWorkText(value: string): string {
  const cleaned = value
    .replace(/^completed:\s*/i, "")
    .replace(/\bproduction readiness\b[:\s-]*/gi, "")
    .replace(/\bprod blocker\b[:\s-]*/gi, "")
    .replace(/\bestate-agent\b/gi, "estate agent")
    .replace(/\bwork-order\b/gi, "work order")
    .replace(/\bfrontend\/native\b/gi, "the frontend and app release flow")
    .replace(/\bbackend\/database\/storage\b/gi, "the backend, database, and storage setup")
    .replace(/\bquote, checkout, stripe\b/gi, "quotes, checkout, payments")
    .replace(/\bStripe\b/g, "payments")
    .replace(/\bCI gate, build, lint baseline, and auth smoke\b/gi, "release checks, build checks, and sign-in checks")
    .replace(/\bCI\b/g, "release checks")
    .replace(/\blint\b/gi, "quality")
    .replace(/\bauth\b/gi, "sign-in")
    .replace(/\blegal route surface\b/gi, "legal pages")
    .replace(/\bsurface exists\b/gi, "are in place")
    .replace(/\bRLS\b/g, "security rules")
    .replace(/\bSupabase\b/g, "the project database")
    .replace(/\bCI\/typecheck\b/gi, "quality checks")
    .replace(/\bDNS\b/g, "domain settings")
    .replace(/\bPRs?\b/g, "review items")
    .replace(/\brepo\b/gi, "project")
    .replace(/\bcommit(s|ted)?\b/gi, "update")
    .replace(/\s+/g, " ")
    .trim();

  return normalizeClientSentence(cleaned || "the latest project work").replace(/\.$/, "");
}

function completedClientTitle(work: string): string {
  return `${clientWorkCategory(work)} is complete`;
}

function completedClientBenefit(work: string): string {
  const benefit = clientBenefitForWork(work);
  return `${benefit} This work is now finished.`;
}

function clientWorkCategory(work: string): string {
  const lower = work.toLowerCase();

  if (/\bwork order\b/.test(lower) && /\bscheduled job\b/.test(lower)) return "Work order scheduling";
  if (/\bmap\b|\broute\b|\blocation\b|\bdirection\b|\baddress\b/.test(lower)) return "Route view improvement";
  if (/\bcheckout\b|\bpayment\b|\binvoice\b|\bbilling\b/.test(lower)) return "Payment and billing flow";
  if (/\bquote\b|\bproposal\b|\bestimate\b/.test(lower)) return "Quote flow improvement";
  if (/\bsign-in\b|\blogin\b|\baccount\b|\buser\b|\bprofile\b/.test(lower)) return "Account access improvement";
  if (/\bnotification\b|\bemail\b|\bmessage\b|\binbox\b|\bcalendar\b|\bschedule\b|\bbooking\b|\bappointment\b/.test(lower)) {
    return "Communication and scheduling update";
  }
  if (/\bdomain\b|\bwebsite\b|\bpage\b|\bpublic\b|\bmarketing\b|\blegal\b/.test(lower)) return "Website update";
  if (/\bsecurity\b|\bpermission\b|\brule\b|\baccess\b|\bpolicy\b/.test(lower)) return "Access and security update";
  if (/\bbackup\b|\brecovery\b|\bmonitoring\b|\brollback\b/.test(lower)) return "Reliability improvement";
  if (/\bfront\s?end\b|\bapp\b|\brelease\b|\bquality\b|\bcheck\b|\bvalidation\b/.test(lower)) return "App stability check";
  if (/\bmigration\b|\bdatabase\b|\bstorage\b|\bdata\b/.test(lower)) return "Data setup cleanup";
  if (/\blovable\b|\bruntime\b|\bdependency\b|\bactive-doc\b|\bplatform\b|\brunbook\b|\bsmoke\b|\blinking\b|\blink\b/.test(lower)) {
    return "Portal cleanup";
  }

  return "Project improvement";
}

function clientBenefitForWork(work: string): string {
  const lower = work.toLowerCase();

  if (/\bwork order\b/.test(lower) && /\bscheduled job\b/.test(lower)) {
    return "Work orders can now become scheduled jobs correctly, so your team can move from request to booking without manual correction.";
  }

  if (/\bmap\b|\broute\b|\blocation\b|\bdirection\b|\baddress\b/.test(lower)) {
    return "Route and location views are more dependable, so jobs are easier to review and plan.";
  }

  if (/\bcheckout\b|\bpayment\b|\binvoice\b|\bbilling\b/.test(lower)) {
    return "The payment and billing flow is more reliable, so clients can complete the process with fewer interruptions.";
  }

  if (/\bquote\b|\bproposal\b/.test(lower)) {
    return "The quote flow is more reliable, so enquiries can move toward approval and scheduling more smoothly.";
  }

  if (/\bsign-in\b|\blogin\b|\bauth\b|\baccount\b/.test(lower)) {
    return "Account access is more reliable, so users can get into the portal and continue their work with fewer access issues.";
  }

  if (/\bnotification\b|\bemail\b|\bmessage\b|\binbox\b/.test(lower)) {
    return "Communication is clearer, so important updates are easier to see and respond to.";
  }

  if (/\bdomain\b|\bwebsite\b|\bpage\b|\bpublic\b|\bmarketing\b/.test(lower)) {
    return "The public-facing experience is more complete, so visitors can find the right information more easily.";
  }

  if (/\bcalendar\b|\bschedule\b|\bbooking\b|\bappointment\b/.test(lower)) {
    return "Scheduling is more dependable, so bookings and follow-up work are easier to track.";
  }

  if (/\bsecurity\b|\bpermission\b|\brule\b|\baccess\b/.test(lower)) {
    return "Access controls are stronger, so the right people can see and use the right parts of the system.";
  }

  if (/\bbackup\b|\brecovery\b|\bmonitoring\b|\brollback\b/.test(lower)) {
    return "The system is easier to monitor and recover, which reduces risk if something unexpected happens.";
  }

  if (/\bmigration\b|\bdatabase\b|\bstorage\b|\bdata\b/.test(lower)) {
    return "The data setup is cleaner and more consistent, which helps keep the portal reliable as more work is added.";
  }

  if (/\blovable\b|\bruntime\b|\bdependency\b|\bactive-doc\b|\bplatform\b|\brunbook\b|\bsmoke\b|\blinking\b|\blink\b/.test(lower)) {
    return "We cleaned up behind-the-scenes portal setup so the dashboard is more stable and easier to maintain.";
  }

  if (/\bfrontend\b|\bapp\b|\brelease\b|\bquality\b|\bcheck\b|\bvalidation\b/.test(lower)) {
    return "The app experience is more stable and ready for release, reducing the chance of user-facing issues.";
  }

  return "We completed a behind-the-scenes improvement that makes the portal smoother and more reliable for your team.";
}

function clientUpdateTitle(work: string): string {
  const title = work
    .replace(/^we\s+(updated|finished|completed)\s+/i, "")
    .replace(/\.$/, "")
    .trim();
  if (!title) return "Project work was updated";
  const compact = title.length > 72 ? `${title.slice(0, 69).trim()}...` : title;
  return normalizeClientSentence(compact);
}

function normalizeClientSentence(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "The latest project work has been updated.";
  const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

function formatDraftAttachmentStatus(attachment: DraftAttachment): string {
  if (attachment.status === "uploading") return `Uploading ${Math.max(1, attachment.progress)}%`;
  if (attachment.status === "failed") return attachment.error ? `Failed: ${attachment.error}` : "Upload failed";
  return `Ready · ${formatBytes(attachment.byteSize)}`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
